import React, { useState } from 'react';
import { Variable, Globe, MapPin, Trash2, Plus, Eye, EyeOff } from 'lucide-react';
import { useVariables } from '../contexts/VariableContext';

const VariableManager = ({ onClose }) => {
  const {
    globalVariables,
    localVariables,
    variableHistory,
    setVariable,
    clearVariables,
  } = useVariables();

  const [activeTab, setActiveTab] = useState('global');
  const [showHistory, setShowHistory] = useState(false);
  const [newVarName, setNewVarName] = useState('');
  const [newVarValue, setNewVarValue] = useState('');

  const handleAddVariable = () => {
    if (newVarName.trim() && newVarValue.trim()) {
      setVariable(newVarName.trim(), newVarValue.trim(), activeTab);
      setNewVarName('');
      setNewVarValue('');
    }
  };

  const formatValue = (value) => {
    if (typeof value === 'string') return `"${value}"`;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'string': return 'text-green-600 bg-green-100';
      case 'number': return 'text-blue-600 bg-blue-100';
      case 'boolean': return 'text-purple-600 bg-purple-100';
      case 'object': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };
  // Hook window event to call deleteVariable via context
  React.useEffect(() => {
    const handler = (e) => {
      const { scope, name, nodeId } = e.detail || {};
      try {
        window.__aiFlowDeleteVar?.(name, scope, nodeId);
      } catch {}
    };
    window.addEventListener('ai-flow-delete-var', handler);
    return () => window.removeEventListener('ai-flow-delete-var', handler);
  }, []);


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Variable size={20} className="text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-800">Variable Manager</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('global')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${
              activeTab === 'global'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Globe size={16} />
            Global Variables ({Object.keys(globalVariables).length})
          </button>
          <button
            onClick={() => setActiveTab('local')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${
              activeTab === 'local'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <MapPin size={16} />
            Local Variables ({Object.keys(localVariables).length})
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${
              showHistory
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {showHistory ? <EyeOff size={16} /> : <Eye size={16} />}
            History ({variableHistory.length})
          </button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {!showHistory ? (
            <>
              {/* Add new variable */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-800 mb-3">
                  Add New {activeTab === 'global' ? 'Global' : 'Local'} Variable
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newVarName}
                    onChange={(e) => setNewVarName(e.target.value)}
                    placeholder="Variable name"
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={newVarValue}
                    onChange={(e) => setNewVarValue(e.target.value)}
                    placeholder="Variable value"
                    className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddVariable}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
              </div>

              {/* Variables list */}
              <div className="space-y-2">
                {activeTab === 'global' ? (
                  Object.entries(globalVariables).length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      No global variables defined
                    </div>
                  ) : (
                    Object.entries(globalVariables).map(([name, data]) => (
                      <div key={name} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Globe size={16} className="text-blue-500" />
                                <button
                                  onClick={() => {
                                    // Delete single global variable by name
                                    const evt = new CustomEvent('ai-flow-delete-var', { detail: { scope: 'global', name } });
                                    window.dispatchEvent(evt);
                                  }}
                                  className="ml-2 text-red-600 hover:bg-red-50 rounded px-2 py-0.5 text-xs"
                                  title="Delete variable"
                                >
                                  Delete
                                </button>

                            <div>
                              <div className="font-medium text-gray-800">{name}</div>
                              <div className="text-sm text-gray-500">
                                {new Date(data.timestamp).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(data.type)}`}>
                              {data.type}
                            </span>
                            <div className="text-sm text-gray-600 max-w-xs truncate">
                              {formatValue(data.value)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )
                ) : (
                  Object.entries(localVariables).length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      No local variables defined
                    </div>
                  ) : (
                    Object.entries(localVariables).map(([key, data]) => {
                      const [nodeId, ...nameParts] = key.split('_');
                      const name = nameParts.join('_');
                      return (
                        <div key={key} className="bg-white border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <MapPin size={16} className="text-green-500" />
                              <div>
                                <div className="font-medium text-gray-800">{name}</div>
                                <div className="text-xs text-gray-500">
                                  Node: {nodeId} • {new Date(data.timestamp).toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(data.type)}`}>
                                {data.type}
                              </span>
                              <div className="text-sm text-gray-600 max-w-xs truncate">
                                {formatValue(data.value)}
                              </div>
                              <button
                                onClick={() => {
                                  const evt = new CustomEvent('ai-flow-delete-var', { detail: { scope: 'local', name, nodeId } });
                                  window.dispatchEvent(evt);
                                }}
                                className="ml-2 text-red-600 hover:bg-red-50 rounded px-2 py-0.5 text-xs"
                                title="Delete variable"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )
                )}
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-800 mb-3">Variable History</h4>
              {variableHistory.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No variable history
                </div>
              ) : (
                variableHistory.slice().reverse().map((entry, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {entry.scope === 'global' ? (
                          <Globe size={16} className="text-blue-500" />
                        ) : (
                          <MapPin size={16} className="text-green-500" />
                        )}
                        <div>
                          <div className="font-medium text-gray-800">{entry.name}</div>
                          <div className="text-xs text-gray-500">
                            {entry.scope} • {new Date(entry.timestamp).toLocaleString()}
                            {entry.nodeId && ` • Node: ${entry.nodeId}`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(entry.type)}`}>
                          {entry.type}
                        </span>
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {formatValue(entry.value)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Use variables in text with: <code className="bg-gray-100 px-1 rounded">{'{{variableName}}'}</code>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => clearVariables(activeTab)}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 size={16} />
              Clear {activeTab === 'global' ? 'Global' : 'Local'}
            </button>
            <button
              onClick={() => clearVariables('all')}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 size={16} />
              Clear All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariableManager;
